// Public no-login confirmation page for the email-approve flow.
// Spec: docs/spec/topics/onboarding/approval-queue.md "Approve from the
// notification email".
//
// Reached from the Approve button in the admin-signup-notification
// email: /admin/signup-requests/approve?token=<email-approve-token>.
// Token-authenticated — no session required. Renders the request summary
// and a single Confirm button that POSTs approveViaToken. The approval is
// a mutation (POST) so email-client / scanner link prefetch of this page
// cannot trigger it.

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../../api/trpc';

export function ApproveSignupRequestPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [personalMessage, setPersonalMessage] = useState('');
  const [done, setDone] = useState<null | 'approved' | 'already_handled'>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);

  const detailQuery = trpc.auth.admin.signupRequests.getByApproveToken.useQuery(
    { token },
    { enabled: token.length > 0, retry: false },
  );

  const approveMutation = trpc.auth.admin.signupRequests.approveViaToken.useMutation({
    onSuccess: (res) => {
      setDone(res.outcome);
      if (res.outcome === 'approved') setWorkspaceName(res.workspaceName);
    },
  });

  // ── Terminal states ───────────────────────────────────────────────────

  if (token.length === 0) {
    return (
      <Shell title="Link incomplete">
        <p style={textStyle}>
          This approve link is missing its token. Open the most recent
          notification email and use the Approve button there.
        </p>
      </Shell>
    );
  }

  if (done === 'approved') {
    return (
      <Shell title="Approved">
        <p style={textStyle}>
          {workspaceName ? <strong>{workspaceName}</strong> : 'The workspace'} is
          being set up and an invitation email is on its way to the requestor.
        </p>
        <p style={mutedStyle}>You can close this page.</p>
      </Shell>
    );
  }

  if (done === 'already_handled') {
    return (
      <Shell title="Already handled">
        <p style={textStyle}>
          This request was already approved or rejected — by another admin or
          from the in-app queue. No action was taken.
        </p>
      </Shell>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <Shell title="Loading…">
        <p style={mutedStyle}>Fetching the request.</p>
      </Shell>
    );
  }

  if (detailQuery.error) {
    return (
      <Shell title="Link invalid or expired">
        <p style={textStyle}>{detailQuery.error.message}</p>
        <p style={mutedStyle}>
          Approve links are valid for 30 days. If yours has expired, open the
          admin queue and approve the request there.
        </p>
      </Shell>
    );
  }

  const sr = detailQuery.data;
  if (!sr) return null;

  if (sr.status !== 'pending') {
    return (
      <Shell title="Already handled">
        <p style={textStyle}>
          This request has already been <strong>{sr.status}</strong>. No action
          was taken.
        </p>
      </Shell>
    );
  }

  // ── Pending: confirmation form ────────────────────────────────────────

  const who = sr.name?.trim() || sr.email;
  const feedback =
    sr.openToFeedbackContact === null || sr.openToFeedbackContact === undefined
      ? null
      : sr.openToFeedbackContact
        ? 'Yes'
        : 'No';

  return (
    <Shell title="Approve access request">
      <p style={mutedStyle}>
        Approving creates a new tenant for <strong>{who}</strong> and emails the
        requestor an invitation link valid for 7 days.
      </p>

      <div style={summaryStyle}>
        <Row label="Name" value={sr.name} />
        <Row label="Email" value={sr.email} />
        <Row label="Company" value={sr.company} />
        <Row label="Role" value={sr.role} />
        <Row label="Team size" value={sr.teamSize} />
        <Row label="Current tools" value={sr.currentTools} />
        <Row label="Heard via" value={sr.source} />
        <Row label="Open to feedback contact" value={feedback} />
        <Row label="Domain" value={sr.emailDomain} />
        <Row label="Submitted" value={new Date(sr.createdAt).toLocaleString()} />
      </div>

      {sr.context?.trim() && (
        <div style={{ marginTop: 12 }}>
          <div style={rowLabelStyle}>Use case</div>
          <p style={{ ...textStyle, whiteSpace: 'pre-wrap', marginTop: 4 }}>
            {sr.context}
          </p>
        </div>
      )}

      <label style={{ display: 'block', marginTop: 20 }}>
        <span style={rowLabelStyle}>
          Optional personal message (rendered verbatim in the approval email)
        </span>
        <textarea
          value={personalMessage}
          onChange={(e) => setPersonalMessage(e.target.value)}
          rows={4}
          maxLength={2000}
          style={textareaStyle}
        />
      </label>

      {approveMutation.error && (
        <div style={errorBoxStyle}>{approveMutation.error.message}</div>
      )}

      <button
        type="button"
        disabled={approveMutation.isPending}
        onClick={() =>
          approveMutation.mutate({
            token,
            personalMessage: personalMessage.trim() || undefined,
          })
        }
        style={{
          ...buttonStyle,
          opacity: approveMutation.isPending ? 0.6 : 1,
          cursor: approveMutation.isPending ? 'default' : 'pointer',
        }}
      >
        {approveMutation.isPending ? 'Approving…' : 'Confirm approval'}
      </button>
    </Shell>
  );
}

// ── Presentational helpers ──────────────────────────────────────────────

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={accentStyle} />
        <div style={{ padding: 32 }}>
          <h1 style={{ fontSize: 20, margin: '0 0 16px' }}>{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (value === null || value === undefined || value.trim().length === 0) return null;
  return (
    <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ flex: '0 0 180px', color: 'var(--color-text-muted)', fontSize: 13 }}>{label}</span>
      <span style={{ flex: 1, fontSize: 14, color: 'var(--color-text-primary)' }}>{value}</span>
    </div>
  );
}

// ── Styles (self-contained shell — this page is reached from an email;
// colors use OBSIDIAN theme tokens so it tracks the app's palette) ──────

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--color-bg)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '48px 16px',
  fontFamily: "'DM Sans', system-ui, sans-serif",
  color: 'var(--color-text-primary)',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 560,
  background: 'var(--color-surface)',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const accentStyle: React.CSSProperties = { height: 3, background: 'var(--color-accent)' };

const textStyle: React.CSSProperties = { fontSize: 15, lineHeight: 1.6, margin: '0 0 8px' };

const mutedStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--color-text-muted)',
  margin: '0 0 8px',
};

const summaryStyle: React.CSSProperties = { marginTop: 16 };

const rowLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: 'var(--color-text-muted)',
  marginBottom: 4,
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 4,
  padding: 8,
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontFamily: 'inherit',
  fontSize: 14,
  boxSizing: 'border-box',
};

// Amber "recommended" CTA — the single primary action on this approve screen
// (OBSIDIAN discipline). This page is reached from an email and renders
// without the app stylesheet, so the amber treatment is inlined via tokens.
const buttonStyle: React.CSSProperties = {
  marginTop: 20,
  padding: '12px 24px',
  background: 'var(--color-brand)',
  color: 'var(--color-brand-ink)',
  border: '1.5px solid var(--color-brand-2)',
  borderRadius: 6,
  fontWeight: 500,
  fontSize: 14,
  fontFamily: 'inherit',
};

const errorBoxStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 12,
  background: 'var(--sev-crit-bg)',
  border: '1px solid var(--sev-crit-border)',
  borderRadius: 6,
  color: 'var(--color-error)',
  fontSize: 14,
};
